import React, {useState} from "react";
import {Button, Col, Form, Modal, Row} from "react-bootstrap";
import {useMutation} from "react-query";
import {Notificator} from "Services/Notificator/Notificator";
import {previewMailType} from "Api/Mutation/MailTypeMutation";
import {FormErrorText} from "Components/Form/FormErrorText";

interface ParamRow {
    key: string,
    value: string
}

interface Props {
    show: boolean,
    mailTypeId: string,
    onClose: () => void
}

export const AdminMailTypePreviewModal: React.FC<Props> = ({show, mailTypeId, onClose}) => {

    const [params, setParams] = useState<ParamRow[]>([{key: '', value: ''}])
    const [validationError, setValidationError] = useState<string>('')
    const [previewResult, setPreviewResult] = useState<{subject: string, html: string} | null>(null)

    const preview = useMutation({
        mutationFn: (paramsObj: Record<string, string>) => previewMailType(mailTypeId, paramsObj),
        onSuccess: (response: any) => {
            setPreviewResult(response.data._result)
        },
        onError: () => {
            Notificator.error('Se ha producido un error al generar la vista previa.')
        }
    })

    const addRow = () => {
        setParams([...params, {key: '', value: ''}])
    }

    const removeRow = (index: number) => {
        setParams(params.filter((_, i) => i !== index))
    }

    const updateRow = (index: number, field: 'key' | 'value', val: string) => {
        const updated = params.map((row, i) => i === index ? {...row, [field]: val} : row)
        setParams(updated)
    }

    const validate = (): Record<string, string> | null => {
        const keys = params.map(r => r.key).filter(k => k !== '')
        const uniqueKeys = new Set(keys)
        if (uniqueKeys.size !== keys.length) {
            setValidationError('Hay claves duplicadas. Cada variable debe tener un nombre único.')
            return null
        }
        const hasInvalid = params.some(r => r.key === '' && r.value !== '')
        if (hasInvalid) {
            setValidationError('Algunas filas tienen valor sin nombre de variable.')
            return null
        }
        setValidationError('')
        return params.reduce((acc: Record<string, string>, {key, value}) => {
            if (key !== '') {
                acc[key] = value
            }
            return acc
        }, {})
    }

    const handleRender = () => {
        const paramsObj = validate()
        if (paramsObj === null) return
        setPreviewResult(null)
        preview.mutate(paramsObj)
    }

    const handleClose = () => {
        setPreviewResult(null)
        setValidationError('')
        onClose()
    }

    return (
        <Modal show={show} onHide={handleClose} size={"xl"} centered>
            <Modal.Header closeButton>
                <Modal.Title>Vista previa del email</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Row>
                    <Col xs={12} md={4}>
                        <h6 className={"mb-3"}>Variables Twig</h6>
                        {params.map((row, index) => (
                            <Row key={index} className={"mb-2 align-items-center"}>
                                <Col xs={5}>
                                    <Form.Control
                                        size={"sm"}
                                        placeholder={"nombre"}
                                        value={row.key}
                                        onChange={(e) => updateRow(index, 'key', e.target.value)}
                                    />
                                </Col>
                                <Col xs={5}>
                                    <Form.Control
                                        size={"sm"}
                                        placeholder={"valor"}
                                        value={row.value}
                                        onChange={(e) => updateRow(index, 'value', e.target.value)}
                                    />
                                </Col>
                                <Col xs={2} className={"text-center"}>
                                    <Button
                                        variant={"outline-danger"}
                                        size={"sm"}
                                        onClick={() => removeRow(index)}
                                        disabled={params.length === 1}
                                    >
                                        ✕
                                    </Button>
                                </Col>
                            </Row>
                        ))}

                        {validationError && <FormErrorText error={validationError} />}

                        <Button
                            variant={"outline-secondary"}
                            size={"sm"}
                            className={"mt-2 mb-3 w-100"}
                            onClick={addRow}
                        >
                            + Añadir variable
                        </Button>

                        <Button
                            className={"purple-btn btn-sm w-100"}
                            onClick={handleRender}
                            disabled={preview.isLoading}
                        >
                            {preview.isLoading ? 'Generando...' : 'Renderizar'}
                        </Button>
                    </Col>

                    <Col xs={12} md={8}>
                        {previewResult && (
                            <>
                                <p className={"mb-1"}>
                                    <strong>Asunto:</strong> {previewResult.subject}
                                </p>
                                <iframe
                                    srcDoc={previewResult.html}
                                    title={"Vista previa del email"}
                                    style={{width: '100%', minHeight: '500px', border: '1px solid #dee2e6', borderRadius: '4px'}}
                                    sandbox={"allow-same-origin"}
                                />
                            </>
                        )}
                        {!previewResult && !preview.isLoading && (
                            <div className={"d-flex align-items-center justify-content-center h-100 text-muted"}>
                                <p>Configura las variables y pulsa "Renderizar" para ver la vista previa.</p>
                            </div>
                        )}
                    </Col>
                </Row>
            </Modal.Body>
            <Modal.Footer>
                <Button variant={"secondary"} onClick={handleClose}>Cerrar</Button>
            </Modal.Footer>
        </Modal>
    )
}
